/**
 * 피드백 위젯(@impakers/debug) 패널의 휠·터치 스크롤 잠금 해제.
 *
 * Radix Dialog/Sheet/Popover 등은 열려 있는 동안 react-remove-scroll 로 스크롤을
 * 잠그는데, 이 잠금은 document 버블 단계에서 "잠금 대상 바깥"의 wheel/touchmove 를
 * 모두 preventDefault 한다. 피드백 위젯은 Dialog 바깥(최상위 z-index)에 그려지므로
 * 모달이 열린 상태에서는 위젯 패널 안에서도 휠 스크롤이 먹통이 된다.
 *
 * 위젯은 pointerdown/click/focusin 은 이미 자체적으로 예외 처리하지만 wheel 은
 * 다루지 않으므로, 앱에서 캡처 단계에 먼저 끼어들어 전파를 끊는다. 전파가 끊기면
 * document 버블에 걸린 react-remove-scroll 리스너가 실행되지 않아 브라우저 기본
 * 스크롤이 그대로 동작한다.
 *
 * 잠금 경로는 두 겹이다. 두 번째는 Radix DismissableLayer 로, 모달이 열려 있는 동안
 * `document.body.style.pointerEvents = "none"` 을 건다(DialogContent 는
 * `disableOutsidePointerEvents: true`). 위젯 패널은 body 아래로 portal 되면서 이 값을
 * 상속하는데, `pointer-events: none` 인 요소는 히트 테스트 대상에서 빠지므로 wheel 의
 * event.target 이 아예 위젯 바깥으로 잡힌다. 그러면 위의 캡처 리스너는 조건이 false 라
 * 동작할 기회조차 없고, 브라우저 기본 스크롤도 일어나지 않는다. 그래서 위젯 표면에
 * `pointer-events: auto` 를 선언해 상속을 끊어 준다.
 *
 * 이 규칙은 `:where()` 로 감싸 명시도를 0 으로 만든다. 상속값보다는 우선하지만 위젯이
 * 자기 클래스로 선언한 `pointer-events: none` 보다는 약해야 하기 때문이다. 특히
 * 주석 마커를 담는 전체 화면 레이어(fixedMarkersLayer)는 `data-impakers-debug` 를
 * 달고 있으면서 화면 전체를 덮고 클릭을 통과시켜야 하므로, 여길 auto 로 덮어쓰면
 * 앱 전체 클릭이 막힌다.
 */

/** 피드백 위젯이 화면에 띄우는 표면들(패널 본체 · 주석 팝업 · 주석 마커). */
export const FEEDBACK_SURFACE_SELECTORS = [
  "[data-impakers-debug]",
  "[data-annotation-popup]",
  "[data-annotation-marker]",
];

/** 스크롤 잠금이 가로채는 이벤트 중 피드백 위젯에서 되살려야 하는 것들. */
export const FEEDBACK_SCROLL_EVENTS = ["wheel", "touchmove"];

export function isInsideFeedbackSurface(target: EventTarget | null): boolean {
  if (!target || typeof (target as Element).closest !== "function") {
    return false;
  }
  const element = target as Element;
  return FEEDBACK_SURFACE_SELECTORS.some(
    (selector) => element.closest(selector) !== null
  );
}

export function handleFeedbackScrollEvent(event: Event): void {
  if (!isInsideFeedbackSurface(event.target)) return;
  event.stopPropagation();
}

/** 주입하는 <style> 태그의 id. 중복 주입을 막는 열쇠이기도 하다. */
export const FEEDBACK_POINTER_EVENTS_STYLE_ID =
  "impakers-feedback-pointer-events-unlock";

/**
 * 모달이 건 `body { pointer-events: none }` 상속을 위젯 표면에서만 끊는 CSS.
 * 모달을 닫은 뒤 body 에 잠금이 남는 경로가 생기더라도 위젯은 영향을 받지 않는다.
 */
export function buildFeedbackPointerEventsCss(): string {
  return `:where(${FEEDBACK_SURFACE_SELECTORS.join(", ")}){pointer-events:auto}`;
}

function installFeedbackPointerEventsUnlock(): () => void {
  if (document.getElementById(FEEDBACK_POINTER_EVENTS_STYLE_ID)) {
    return () => {};
  }

  const style = document.createElement("style");
  style.id = FEEDBACK_POINTER_EVENTS_STYLE_ID;
  style.textContent = buildFeedbackPointerEventsCss();
  document.head.appendChild(style);

  return () => style.remove();
}

/** 캡처 단계 리스너와 pointer-events 해제 CSS 를 걸고, 해제 함수를 돌려준다. */
export function installFeedbackScrollUnlock(): () => void {
  if (typeof document === "undefined") return () => {};

  for (const eventName of FEEDBACK_SCROLL_EVENTS) {
    document.addEventListener(eventName, handleFeedbackScrollEvent, true);
  }
  const uninstallPointerEvents = installFeedbackPointerEventsUnlock();

  return () => {
    for (const eventName of FEEDBACK_SCROLL_EVENTS) {
      document.removeEventListener(eventName, handleFeedbackScrollEvent, true);
    }
    uninstallPointerEvents();
  };
}
